import { Component } from '@theme/component';

const selectors = {
  HEADING: 'h1, h2, h3, h4, h5, h6',
  ANCHOR_SECTION: '[data-anchor-section]',
  SHOPIFY_SECTION: '[id^="shopify-section-"]',
  SECTION: ':scope > [id^="shopify-section-"]',
};

const classes = {
  LINK: 'anchor-links__item',
};

const attributes = {
  HREF: 'href',
  ON_CLICK: 'on:click',
  STICKY: 'data-sticky',
  SCROLL_SETTLE_TIMEOUT: 1500,
  SUBHEADING: '[data-subheading]',
  CLICK_ACTION: '/scrollToSection',
  HEADER_HEIGHT_VAR: '--header-group-height',
};

/**
 * Scans sibling sections after itself for headings and builds sticky in-page navigation.
 * @typedef {Object} Refs
 * @property {HTMLElement} nav
 * @property {HTMLDivElement} progress
 * @extends {Component<Refs>}
 */
export class AnchorLinks extends Component {
  requiredRefs = ['nav', 'progress'];

  /** @type {Map<string, HTMLAnchorElement>} */
  #linkMap = new Map();
  /** @type {string | null} */
  #activeId = null;
  /** @type {AbortController | null} */
  #abort = null;

  #rafId = 0;
  #headerHeight = 0;
  #progressReady = false;
  #isClickScrolling = false;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();

    const { signal } = this.#abort;

    this.#buildNav();
    this.#updateHeaderHeight(); // must run before #scheduleUpdate so sticky check has correct headerHeight on first frame
    this.#scheduleUpdate();

    window.addEventListener('resize', this.#onResize, { signal });
    window.addEventListener('scroll', this.#scheduleUpdate, { passive: true, signal });
  }

  #updateHeaderHeight() {
    this.#headerHeight =
      parseInt(getComputedStyle(document.body).getPropertyValue(attributes.HEADER_HEIGHT_VAR)) || 0;
  }

  #onResize = () => {
    this.#updateHeaderHeight();
    this.#scheduleUpdate();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    cancelAnimationFrame(this.#rafId);
  }

  #scheduleUpdate = () => {
    cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => {
      this.toggleAttribute(
        attributes.STICKY,
        this.getBoundingClientRect().top <= this.#headerHeight,
      );
      this.#updateActive();
    });
  };

  /**
   * Scans sibling sections and builds the navigation links dynamically.
   * If a section has a heading, it creates an `<a>` tag pointing to that section's ID.
   */
  #buildNav() {
    const { nav } = this.refs;

    if (!nav) return;

    const sections = this.#getSiblingSections();
    const fragment = document.createDocumentFragment();

    for (const section of sections) {
      const label = this.#getSectionLabel(section);

      if (!label || !section.id) continue;

      const link = document.createElement('a');

      Object.assign(link, {
        href: `#${section.id}`,
        className: classes.LINK,
        textContent: label,
      });
      link.setAttribute(attributes.ON_CLICK, attributes.CLICK_ACTION);

      fragment.append(link);
      this.#linkMap.set(section.id, link);
    }

    nav.append(fragment);
  }

  /** @returns {Element[]} */
  #getSiblingSections() {
    const main = this.closest('main');
    const sectionEl = this.closest(selectors.SHOPIFY_SECTION);

    if (!main || !sectionEl) return [];

    const allSections = [...main.querySelectorAll(selectors.SECTION)];

    return allSections
      .slice(allSections.indexOf(sectionEl) + 1)
      .filter((s) => s.querySelector(selectors.ANCHOR_SECTION));
  }

  /**
   * @param {Element} section
   * @returns {string | null}
   */
  #getSectionLabel(section) {
    return (
      section.querySelector(attributes.SUBHEADING)?.textContent?.trim() ||
      section.querySelector(selectors.HEADING)?.textContent?.trim() ||
      null
    );
  }

  /**
   * Calculates which section is currently in the viewport based on scroll position.
   * Updates the active link state and triggers UI updates (progress bar & centering link).
   * Skips logic if the user is currently smooth-scrolling via a nav link click.
   */
  #updateActive() {
    if (this.#isClickScrolling) return;

    const offset = this.#getScrollOffset();
    let activeId = null;
    let activeLink = null;

    for (const [id, link] of this.#linkMap) {
      const el = document.getElementById(id);
      const isActive = el ? el.getBoundingClientRect().top <= offset + 1 : false;

      if (isActive) {
        activeId = id;
        activeLink = link;
      }
    }

    if (!activeId || !activeLink) return;

    const changed = activeId !== this.#activeId;
    this.#activeId = activeId;

    this.#updateProgress(activeLink);

    if (changed) {
      this.#scrollLinkIntoView(activeLink);
    }
  }

  /**
   * Moves and resizes the progress indicator straight beneath the active link.
   * Disables CSS transitions on the initial load to prevent an unwanted startup animation.
   * @param {HTMLAnchorElement} link - The currently active link element.
   */
  #updateProgress(/** @type {HTMLAnchorElement} */ link) {
    const { progress } = this.refs;

    if (!progress) return;

    const skipTransition = !this.#progressReady;

    if (skipTransition) {
      this.#progressReady = true;
      progress.style.transition = 'none';
    }

    progress.style.width = `${link.offsetWidth}px`;
    progress.style.transform = `translateX(${link.offsetLeft}px)`;

    if (skipTransition) {
      progress.offsetHeight;
      progress.style.transition = '';
    }
  }

  /**
   * Auto-scrolls the horizontal navigation container so the active link is always visible.
   * Useful when the user scrolls the page down and the active link changes off-screen.
   * @param {HTMLAnchorElement} link - The currently active link element.
   */
  #scrollLinkIntoView(/** @type {HTMLAnchorElement} */ link) {
    const linkRect = link.getBoundingClientRect();
    const hostRect = this.getBoundingClientRect();

    if (linkRect.left < hostRect.left || linkRect.right > hostRect.right) {
      link.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  /**
   * Declarative click handler (`on:click`) for the anchor links.
   * Prevents default hash jumping, locks the `#updateActive` observer, and smooth-scrolls
   * the page to the target section minus the sticky header offset. Waits for the scroll to settle.
   * @param {Event} event
   */
  scrollToSection(event) {
    event.preventDefault();

    const anchor = /** @type {HTMLAnchorElement} */ (event.target);
    const id = anchor.getAttribute(attributes.HREF)?.slice(1);

    if (!id) return;

    const section = document.getElementById(id);

    if (!section) return;

    this.#isClickScrolling = true;
    this.#activeId = id;

    const clickedLink = this.#linkMap.get(id);

    if (clickedLink) {
      this.#updateProgress(clickedLink);
      this.#scrollLinkIntoView(clickedLink);
    }

    const offset = this.#getScrollOffset();

    window.scrollTo({
      top: section.getBoundingClientRect().top + window.scrollY - offset,
      behavior: 'smooth',
    });

    const scrollAbortController = new AbortController();

    const settle = () => {
      if (scrollAbortController.signal.aborted) return;

      scrollAbortController.abort();
      clearTimeout(fallbackTimer);
      this.#isClickScrolling = false;
      this.#activeId = id;

      if (clickedLink) this.#updateProgress(clickedLink);
    };

    window.addEventListener('scrollend', settle, {
      once: true,
      signal: scrollAbortController.signal,
    });
    const fallbackTimer = setTimeout(settle, attributes.SCROLL_SETTLE_TIMEOUT);
  }

  #getScrollOffset() {
    return this.#headerHeight + this.offsetHeight;
  }
}

if (!customElements.get('anchor-links')) {
  customElements.define('anchor-links', AnchorLinks);
}
